import React, { useEffect, useState } from 'react';
import {
    Col,
    Form,
    FormControl,
    FormGroup,
    FormLabel,
} from 'react-bootstrap';
import {
    DatePicker,
    Card,
    Checkbox,
    CustomRadio,
    LoaderBtn,
    Captcha,
} from 'components';
import { mapAPIError, personalDataProcessingPolicy } from '_helpers';
import { Sex } from '../../api/ApiClient';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { connect } from 'react-redux';
import moment from 'moment';
import styles from './Step.module.scss';

const defaultState: FormState = {
    sex: '' as Sex,
    personalChecked: false,
    birthDate: null,
};

const defaultFormErrors = {
    sex: '',
    personalChecked: '',
    birthDate: '',
    captcha: '',
};

const Step3 = ({
    apiError, confirmAccount, authProcessing, token, password, verificationCode, email,
}: Props) => {
    const [form, setForm] = useState(defaultState);
    const [errors, setErrors] = useState(defaultFormErrors);
    const [captcha, setCaptcha] = React.useState('');
    const [captchaValid, setCaptchaValid] = React.useState(true);

    useEffect(() => {
        setErrors({
            ...errors,
            ...mapAPIError(apiError),
        });
    }, [apiError]);

    const doValidate = (formData: FormState) => {
        const validateErrors = { ...errors };

        const setE = (isNotValid: boolean) => (isNotValid ? 'Error' : '');

        validateErrors.birthDate = setE(!formData.birthDate);
        validateErrors.sex = setE(!formData.sex);
        validateErrors.captcha = '';

        const isCaptchaValid = apiError && apiError.failures && apiError.failures.captcha ? captcha.length === 4 : true;
        const isFormValid = Object.values(validateErrors).every(err => !err);

        setCaptchaValid(isCaptchaValid);
        setErrors(validateErrors);

        return isFormValid && isCaptchaValid;
    };

    const handleBirthDayChange = (ymd: string, time: moment.Moment) => {
        const newForm = {
            ...form,
            birthDate: time,
        };

        setForm(newForm);
        doValidate(newForm);
    };

    const handleSexChange = (sex: Sex) => {
        const newForm = {
            ...form,
            sex,
        };

        setForm(newForm);
        doValidate(newForm);
    };

    const handleToggleCheck = (targetNodeName: string, name: string) => {
        if (targetNodeName !== 'DIV' && targetNodeName !== 'LABEL') return;
        setForm({
            ...form,
            [name]: !form[name],
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!doValidate(form)) return;

        await confirmAccount({
            birthDate: moment(form.birthDate || ''),
            sex: form.sex,
            token,
            password,
            verificationCode,
        });

        setCaptcha('');
    };

    const renderSexElement = (sex: Sex) => (
        <CustomRadio
          blue
          option={sex}
          name="sex"
          label={SexTranslation[sex]}
          checked={form.sex === sex}
          onClick={() => handleSexChange(sex)}
        />
    );

    return (
            <Col className="auth-forms--container">
                <form onSubmit={handleSubmit}>
                    <Card
                      title="Регистрация"
                      apiError={apiError}
                      legend={(
                            <div>
                                <Form.Control.Feedback className="d-block" type="invalid">
                                    {apiError && (!(apiError.failures && apiError.failures.length) || apiError.type === 'captcha') && apiError.message}
                                </Form.Control.Feedback>
                                <LoaderBtn
                                  variant="primary"
                                  type="submit"
                                  disabled={!form.personalChecked || Boolean(authProcessing)}
                                  loading={Boolean(authProcessing)}
                                >
                                    Зарегистрироваться
                                </LoaderBtn>
                            </div>
                        )}
                      ftTextCenter
                    >
                        <div>
                            <div className={styles.sub_title}>
                                дополнительная информация
                            </div>

                            <FormGroup data-test-email-group>
                                <FormLabel>Электронная почта</FormLabel>
                                <FormControl
                                  autoFocus
                                  name="email"
                                  value={email}
                                  disabled
                                />
                            </FormGroup>

                            <FormGroup data-test-date-group>
                                <FormLabel>Дата рождения</FormLabel>
                                <DatePicker onChange={handleBirthDayChange} isInvalid={errors.birthDate ? 'Заполните дату рождения' : undefined} />
                            </FormGroup>

                            <FormGroup className={styles.phone_form_container} data-test-sex-group>
                                <div className="sex-container">
                                    {renderSexElement(Sex.Male)}
                                    {renderSexElement(Sex.Female)}
                                </div>
                                <FormControl hidden isInvalid={Boolean(errors.sex)} />
                                <Form.Control.Feedback type="invalid">
                                    Выберите пол
                                </Form.Control.Feedback>
                            </FormGroup>

                            <FormGroup>
                                <div className="required-checkboxes-container">
                                    <div onClick={(e: any) => handleToggleCheck(e.target.nodeName, 'personalChecked')} data-test-first-checkbox>
                                        <Checkbox checked={form.personalChecked} number="1" />
                                        <label>
                                            Я выражаю свое согласие на обработку моих персональных данных в
                                            соответствии с
                                          <a target="__blank" style={{ display: 'block' }} href={personalDataProcessingPolicy}>
                                                Политикой обработки и защиты
                                                персональных данных
                                          </a>
                                        </label>
                                    </div>
                                </div>
                            </FormGroup>
                            <Captcha captcha={captcha} captchaValid={captchaValid} apiError={apiError} setCaptcha={setCaptcha} />
                        </div>
                    </Card>
                </form>
            </Col>
    );
};

const mapState = (state: IRootState) => ({
    apiError: state.errors.error,
    authProcessing: Boolean(state.loading.effects.auth.confirmAccountAsync),
});

const mapDispatch = (dispatch: Dispatch) => ({
    confirmAccount: dispatch.auth.confirmAccountAsync,
});

export default connect(mapState, mapDispatch)(Step3);

type connectedProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;
type Props = connectedProps & IOwnProps;

interface IOwnProps {
    email: string;
    token: string;
    password: string;
    verificationCode: string;
}

interface FormState {
    sex: Sex;
    personalChecked: boolean;
    birthDate: moment.Moment | null;
    [key: string]: string | boolean | moment.Moment | null;
}

enum SexTranslation {
    'male' = 'Мужской',
    'female' = 'Женский',
}
